import Layout from '@/components/layout';
import SekeletonDashboardLayout from '@/components/SkeletonLayout/SekeletonDashboardLayout';
import { SkeletonWrapper } from '@/components/SkeletonLayout/styled';
import { config } from '@/config';
import { PricingPlan, ROLE_SHOP_THEME, SETTING_TYPE } from '@/constants/enum';
import { openCrisp } from '@/helpers';
import { useGetOrderQuotaQuery, useGetShopMetafieldsQuery, useGetShopThemesQuery } from '@/redux/query';
import { BlockStack, Button, InlineStack } from '@shopify/polaris';
import { ChatIcon, ConfettiIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderInforBanner } from './component/OrderInforBanner';
import { SchedulingWidgetInfor } from './component/SchedulingWidgetInfo';
import { SupportCard } from './component/SupportCard';
import { SetupInformation } from './component/SetupInfomation';
import { SaleInformation } from './component/SaleInformation';
import { Setting } from '@/types/components/metafield';
import { initCannyChangelog, renderCanny, useCanny } from '@/hooks/useCanny';
import RegularText from '@/components/RegularText';
import { CustomItalicText } from './styled';
import { PricingBannerDashboard } from './component/PricingBanner';

const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const { token, isLoadingCanny } = useCanny();

  const { data: orderQuotaData, isLoading: isLoadingOrderQuota } = useGetOrderQuotaQuery({ shop: config.shop });

  const { data: shopThemesData, isLoading: isLoadingShopThemes } = useGetShopThemesQuery({ shop: config.shop });

  const { data: shopMetafieldsData, isLoading: isLoadingMetafields } = useGetShopMetafieldsQuery(
    { shop: config.shop },
    { refetchOnMountOrArgChange: true },
  );

  const generalSettingData = useMemo(
    () => shopMetafieldsData?.data?.app?.data?.shopGeneral,
    [shopMetafieldsData?.data?.app?.data?.shopGeneral],
  );

  const widgetSettingData = useMemo(
    () => shopMetafieldsData?.data?.app?.data?.appearance,
    [shopMetafieldsData?.data?.app?.data?.appearance],
  );

  const settingData = useMemo(
    () => shopMetafieldsData?.data?.app?.data?.settings,
    [shopMetafieldsData?.data?.app?.data?.settings],
  );

  const getEnableMethods = (settings?: Setting[]) => {
    if (settings) {
      const isEnablePickup = settings.some((s) => s.type === SETTING_TYPE.PICKUP && s.location?.isEnablePickup);

      const isEnableDelivery = settings.some(
        (s) => s.type === SETTING_TYPE.DELIVERY && s.location?.isEnableDelivery && s.isActive,
      );

      const isEnableShipping = settings.some((s) => s.type === SETTING_TYPE.SHIPPING && s.isActive);

      return { isEnablePickup, isEnableDelivery, isEnableShipping };
    }
  };

  const appEmbedId = process.env.REACT_APP_SHOPIFY_THEME_APP_EXTENSION_ID;

  const mainTheme = useMemo(() => {
    return shopThemesData?.find((item) => item.role === ROLE_SHOP_THEME.MAIN);
  }, [shopThemesData]);

  const themeId = useMemo(() => {
    return mainTheme?.id.split('/')[mainTheme?.id.split('/').length - 1];
  }, [mainTheme?.id]);

  const isActiveEmbed = useMemo(() => {
    const file = mainTheme?.files.find((item) => item.filename === 'config/settings_data.json')?.file;
    const blocks = file?.current?.blocks;
    if (blocks && appEmbedId) {
      const appEmbedBlock = Object.values(blocks).find((block) => block.type.includes(appEmbedId));
      if (appEmbedBlock) {
        return !appEmbedBlock.disabled;
      }
    }
    return false;
  }, [appEmbedId, mainTheme?.files]);

  const locationOptions = Array.from(
    new Map(
      settingData
        ?.filter((item) => item.location && item.location.id != null)
        .map((item) => [
          item.location?.id,
          {
            value: String(item.location?.id),
            label: item.location?.name || '',
          },
        ]),
    ).values(),
  );

  useEffect(() => {
    if (!isLoadingShopThemes && !isLoadingOrderQuota && !isLoadingMetafields && !isLoadingCanny && token) {
      renderCanny(token);
      initCannyChangelog();
    }
  }, [isLoadingShopThemes, isLoadingOrderQuota, isLoadingMetafields, token, isLoadingCanny]);

  return (
    <Layout title={t('page.title')}>
      {isLoadingShopThemes || isLoadingOrderQuota || isLoadingMetafields ? (
        <SkeletonWrapper>
          <SekeletonDashboardLayout />
        </SkeletonWrapper>
      ) : (
        <BlockStack gap="400">
          <PricingBannerDashboard isOpen={Boolean(generalSettingData?.showBannerOfferPricing)} />
          {generalSettingData?.plan && generalSettingData?.plan !== PricingPlan.Enterprise && (
            <OrderInforBanner
              currentPlan={generalSettingData?.plan}
              quota={Number(orderQuotaData?.data.quota)}
              used={Number(orderQuotaData?.data.used)}
            />
          )}
          <InlineStack blockAlign="center" align="space-between">
            <CustomItalicText>
              <RegularText>Welcome to DingDoong 🎉</RegularText>
            </CustomItalicText>
            <InlineStack gap="200" blockAlign="center" align="end">
              <Button variant="primary" icon={ChatIcon} onClick={openCrisp}>
                {t('button.chat')}
              </Button>
              <div data-canny-changelog>
                <Button icon={ConfettiIcon}>{t('button.change')}</Button>
              </div>
            </InlineStack>
          </InlineStack>
          <SchedulingWidgetInfor
            currentPlan={generalSettingData?.plan}
            quota={Number(orderQuotaData?.data.quota)}
            used={Number(orderQuotaData?.data.used)}
            shop={config.shop}
            isActiveEmbed={isActiveEmbed}
            appEmbedId={appEmbedId}
            themeId={themeId}
            position={widgetSettingData?.widgetPosition.displayOnPage}
            hasEnableMethods={getEnableMethods(settingData)}
          />
          <SetupInformation
            checkedStep={generalSettingData?.checkedSetupStep.split(',') || []}
            isActiveEmbed={isActiveEmbed}
            themeId={themeId}
            hasEnableMethods={getEnableMethods(settingData)}
          />
          <SaleInformation listLocationName={locationOptions} hasEnableMethods={getEnableMethods(settingData)} />
          <SupportCard />
        </BlockStack>
      )}
    </Layout>
  );
};
export default memo(Dashboard);
